import { useEffect, useRef, useState } from 'react'
import { DashboardScreen } from './components/DashboardScreen'
import { DevBlogPage } from './components/DevBlogPage'
import { GameView } from './components/GameView'
import { TowerHome } from './components/TowerHome'
import { TowerLogPage } from './components/TowerLogPage'
import { loadHelpdesk, subscribeHelpdeskIdentify } from './auth/helpdesk'
import { ConsentBanner } from './components/ConsentBanner'
import { hasHelpdeskConsent, onConsentChange } from './data/consent'
import { findLevel } from './data/loadLevel'
import { DIFFICULTY_ORDER, type DifficultyName } from './data/userProfile'
import { useProfileStore } from './state/profileStore'
import { FULL_UNLOCK } from './data/packs'
import {
  logPageView,
  logPurchaseCompleted,
  setAnalyticsUserId,
} from './analytics/AnalyticsManager'
import { exitDestination } from './util/navigation'

/// The dedicated route for the "Talk to the developer" board.
/// Firebase Hosting rewrites `**` → /index.html, so the deep URL
/// just serves the SPA.
const DEV_BLOG_PATH = '/talktothedeveloper'

/// The dedicated route for the public Tower Log stats page. Same
/// catch-all-rewrite trick as DEV_BLOG_PATH.
const TOWER_LOG_PATH = '/towerlog'

/// Two-screen router. Boots into the home / level-select screen
/// unless the URL deep-links into a specific level via `?level=N51`,
/// in which case we go straight to the game. Auth + profile load
/// happen synchronously on first render so the home screen has the
/// signed-in badge ready.
type Screen =
  | { kind: 'home' }
  | { kind: 'game'; levelId: string }
  /// `initialPostId` deep-links into a specific thread on the dev
  /// blog. Used by the Phase 2 inbox banner on HomeScreen — tapping
  /// "the developer replied to X" should open that thread directly,
  /// not dump the user onto the list view.
  | { kind: 'devblog'; initialPostId?: string }
  /// The public Tower Log stats page (/towerlog).
  | { kind: 'towerlog' }
  /// Private revenue/funnel dashboard, reached only via `?dashboard` and
  /// gated to admin emails. Undiscoverable from any nav.
  | { kind: 'dashboard' }

/// Emails allowed to view the dashboard (client-side UX gate only — the
/// `getDashboardMetrics` Cloud Function enforces the real check).
const DASHBOARD_ADMIN_EMAILS = new Set<string>([
  'craig@liveflatout.org',
  'kennedy.craig@gmail.com',
])

function initialScreen(): Screen {
  // Strip any trailing slashes so `/talktothedeveloper/` matches too.
  const path = window.location.pathname.replace(/\/+$/, '')
  if (path === DEV_BLOG_PATH) {
    // Deep-link support: `/talktothedeveloper?post=<id>` opens the
    // thread directly. iOS uses this from the Settings inbox row and
    // from notification-tap handlers; web uses it from the home-
    // screen inbox banner (which sets state directly, not the URL,
    // but the same query-param path keeps the surfaces symmetric).
    const postId = new URLSearchParams(window.location.search).get('post')
    return { kind: 'devblog', initialPostId: postId && postId.length > 0 ? postId : undefined }
  }
  if (path === TOWER_LOG_PATH) return { kind: 'towerlog' }
  const search = new URLSearchParams(window.location.search)
  if (search.has('dashboard')) return { kind: 'dashboard' }
  const param = search.get('level')
  if (param && findLevel(param)) return { kind: 'game', levelId: param }
  return { kind: 'home' }
}

type PurchaseToast =
  | { kind: 'success'; sessionId: string | null }
  | { kind: 'cancelled' }
  | null

function initialPurchaseToast(): PurchaseToast {
  const params = new URLSearchParams(window.location.search)
  const flag = params.get('purchase')
  if (flag === 'success') {
    return { kind: 'success', sessionId: params.get('session_id') }
  }
  if (flag === 'cancelled') return { kind: 'cancelled' }
  return null
}

function clearPurchaseParams(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('purchase')
  url.searchParams.delete('session_id')
  window.history.replaceState({}, '', url)
}

export default function App() {
  const [screen, setScreen] = useState<Screen>(() => initialScreen())
  const profileState = useProfileStore((s) => s.state)
  const bootProfile = useProfileStore((s) => s.boot)
  const [purchaseToast, setPurchaseToast] = useState<PurchaseToast>(
    () => initialPurchaseToast(),
  )

  useEffect(() => {
    void bootProfile('TUTORIAL')
  }, [bootProfile])

  // Marks this browser as having reached the game SPA at least once, so
  // the static landing page (`index.html` / `src/landing/main.ts`) can
  // swap its free-play CTA to "Jump back in" on a later visit. Best
  // effort only — private browsing / disabled storage just means the
  // landing page keeps showing the default label.
  useEffect(() => {
    try {
      window.localStorage.setItem('fa_returning', '1')
    } catch {
      // Storage unavailable — nothing to do.
    }
  }, [])

  // Browser back/forward: re-derive the screen from the URL. The
  // app otherwise only uses pushState/replaceState, so this is the
  // single place that reacts to history navigation.
  useEffect(() => {
    const onPop = () => setScreen(initialScreen())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Subscribe Firebase ID-token changes to the helpdesk widget so
  // signed-in / anonymous players land on their existing thread
  // when they hit "Contact Support". The Beacon loader itself is
  // gated on consent — load now if we already have it; otherwise
  // wait for the consent banner to grant. Bridge is
  // fire-and-forget; unsubscribes on unmount.
  useEffect(() => {
    if (hasHelpdeskConsent()) loadHelpdesk()
    const unsubConsent = onConsentChange((level) => {
      if (level === 'all') loadHelpdesk()
    })
    const unsubIdentify = subscribeHelpdeskIdentify()
    return () => {
      unsubConsent()
      unsubIdentify()
    }
  }, [])

  // Strip the ?purchase=... params from the URL on first paint so a
  // refresh / share doesn't re-trigger the toast. The toast itself
  // auto-dismisses after a few seconds; success stays a touch longer.
  useEffect(() => {
    if (!purchaseToast) return
    if (purchaseToast.kind === 'success') {
      logPurchaseCompleted({
        productId: FULL_UNLOCK.stripeProductId,
        productName: FULL_UNLOCK.displayName,
        priceUSD: FULL_UNLOCK.defaultPriceUSD,
        transactionId: purchaseToast.sessionId ?? 'unknown',
      })
    }
    clearPurchaseParams()
    const ms = purchaseToast.kind === 'success' ? 6000 : 4000
    const t = window.setTimeout(() => setPurchaseToast(null), ms)
    return () => window.clearTimeout(t)
  }, [purchaseToast])

  // Bind the Firebase UID for cross-platform analytics linkage as
  // soon as the profile boot resolves.
  useEffect(() => {
    if (profileState.kind === 'ready') {
      setAnalyticsUserId(profileState.user.uid)
    }
  }, [profileState])

  // Fire a page_view on every logical screen change. The SPA now only
  // ever boots at /play, but it moves between several logical "pages"
  // (home, game, dev blog, tower log, dashboard) via pushState/replaceState
  // rather than a real navigation, so GA's own automatic pageview tracking
  // never sees most of them — this fills the gap explicitly.
  useEffect(() => {
    logPageView(window.location.pathname || '/play')
  }, [screen.kind])

  const goHome = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('level')
    url.searchParams.delete('difficulty')
    url.searchParams.delete('dashboard')
    url.pathname = '/play'
    window.history.replaceState({}, '', url)
    setScreen({ kind: 'home' })
  }

  // True only when the dev blog / Tower Log was opened from inside the
  // game (TowerHome buttons). Direct URL entries (the static landing
  // page's links, the FIAM banner, reply-notification emails) boot the
  // SPA straight onto those screens and leave this false, so their
  // "home" exit returns to the site's front door instead of dropping
  // a non-player into the game.
  const enteredSharedSurfaceInApp = useRef(false)

  const goDevBlog = (initialPostId?: string) => {
    enteredSharedSurfaceInApp.current = true
    window.history.pushState({}, '', DEV_BLOG_PATH)
    setScreen({ kind: 'devblog', initialPostId })
  }

  const exitSharedSurface = () => {
    if (exitDestination(enteredSharedSurfaceInApp.current) === '/play') {
      window.history.pushState({}, '', '/play')
      setScreen({ kind: 'home' })
      return
    }
    // The landing page is a static document outside the SPA — a full
    // navigation is required; pushState would just re-render the game.
    window.location.assign('/')
  }

  const exitDevBlog = exitSharedSurface

  const goTowerLog = () => {
    enteredSharedSurfaceInApp.current = true
    window.history.pushState({}, '', TOWER_LOG_PATH)
    setScreen({ kind: 'towerlog' })
  }

  const exitTowerLog = exitSharedSurface

  const goPlay = (levelId: string, difficulty: DifficultyName) => {
    const url = new URL(window.location.href)
    url.searchParams.set('level', levelId)
    if (DIFFICULTY_ORDER.includes(difficulty)) {
      url.searchParams.set('difficulty', difficulty)
    }
    window.history.replaceState({}, '', url)
    setScreen({ kind: 'game', levelId })
  }

  // Auth + profile boot is fast (one anonymous-auth round trip plus
  // one Firestore read), but if the network is slow we want a
  // visible state. Errors don't block — we render the app anyway
  // and let the user retry; profile features just won't persist.
  if (profileState.kind === 'loading' || profileState.kind === 'idle') {
    return <BootScreen message="Signing in…" />
  }
  if (profileState.kind === 'error') {
    return <BootScreen message={`Sign-in failed: ${profileState.message}`} error />
  }

  if (screen.kind === 'dashboard') {
    const email = (
      profileState.kind === 'ready' ? profileState.user.email ?? '' : ''
    ).toLowerCase()
    if (!DASHBOARD_ADMIN_EMAILS.has(email)) {
      // Two different situations wear the same face here, and saying so
      // matters: Firebase persists a session PER ORIGIN, so arriving at
      // flatoutatc.com with a session established on flatout-atc-game.web.app
      // makes you a fresh ANONYMOUS user with no email — which read as a
      // permissions failure with no way forward (Craig, 2026-08-23).
      // Anonymous gets the way in; a signed-in non-admin still gets the flat
      // refusal, because for them it really is a gate.
      return email
        ? <BootScreen message={`Not authorized — ${email} is not an admin account.`} error />
        : <DashboardSignIn />
    }
    return (
      <div className="relative h-full w-full">
        <DashboardScreen onHome={goHome} />
        <ConsentBanner />
      </div>
    )
  }

  if (screen.kind === 'devblog') {
    return (
      <div className="relative h-full w-full">
        <DevBlogPage onHome={exitDevBlog} initialPostId={screen.initialPostId} />
        {purchaseToast && (
          <PurchaseToastView toast={purchaseToast} onDismiss={() => setPurchaseToast(null)} />
        )}
        <ConsentBanner />
      </div>
    )
  }

  if (screen.kind === 'towerlog') {
    return (
      <div className="relative h-full w-full">
        <TowerLogPage onHome={exitTowerLog} />
        {purchaseToast && (
          <PurchaseToastView toast={purchaseToast} onDismiss={() => setPurchaseToast(null)} />
        )}
        <ConsentBanner />
      </div>
    )
  }

  if (screen.kind === 'home') {
    return (
      <div className="relative h-full w-full">
        <TowerHome onPlay={goPlay} onOpenDevBlog={goDevBlog} onOpenTowerLog={goTowerLog} />
        {purchaseToast && (
          <PurchaseToastView
            toast={purchaseToast}
            onDismiss={() => setPurchaseToast(null)}
          />
        )}
        <ConsentBanner />
      </div>
    )
  }
  return (
    <div className="relative h-full w-full">
      <GameView levelId={screen.levelId} onHome={goHome} />
      {purchaseToast && (
        <PurchaseToastView
          toast={purchaseToast}
          onDismiss={() => setPurchaseToast(null)}
        />
      )}
      <ConsentBanner />
    </div>
  )
}

function PurchaseToastView(props: { toast: PurchaseToast; onDismiss: () => void }) {
  if (!props.toast) return null
  const success = props.toast.kind === 'success'
  return (
    <div className="pointer-events-none fixed left-1/2 top-6 z-50 -translate-x-1/2">
      <div
        className={
          'pointer-events-auto flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold shadow-2xl ring-1 backdrop-blur-sm ' +
          (success
            ? 'bg-emerald-900/90 text-emerald-50 ring-emerald-400/40'
            : 'bg-slate-900/90 text-slate-100 ring-white/15')
        }
      >
        <span className="text-lg leading-none">{success ? '✅' : '↩️'}</span>
        <div>
          <div>
            {success
              ? 'Full Game Unlocked — every level is yours.'
              : 'Checkout cancelled — no charge.'}
          </div>
          {success && (
            <div className="mt-0.5 text-xs font-normal text-emerald-200/80">
              Locked pins on the map will unlock momentarily.
            </div>
          )}
        </div>
        <button
          onClick={props.onDismiss}
          className={
            'ml-2 rounded px-2 py-1 text-xs font-semibold transition-colors ' +
            (success
              ? 'text-emerald-200/80 hover:bg-emerald-800/80 hover:text-white'
              : 'text-slate-300 hover:bg-white/10 hover:text-white')
          }
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

/// The dashboard's own sign-in. Reached only when someone opens `?dashboard`
/// on a session with no email at all — i.e. the anonymous session every first
/// visit to an origin gets. Deliberately says WHICH origin you are on, since
/// the whole confusion is that a session on one host is invisible to another.
function DashboardSignIn() {
  const signIn = useProfileStore((s) => s.signInWithGoogle)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function go() {
    setBusy(true)
    setError(null)
    const result = await signIn()
    setBusy(false)
    if (!result.ok) setError(result.message ?? 'Sign-in failed.')
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-950 px-6">
      <div className="w-full max-w-sm rounded-lg bg-slate-900/70 p-6 text-center ring-1 ring-white/10">
        <h1 className="text-base font-semibold text-white">Dashboard</h1>
        <p className="mt-2 text-sm text-white/70">
          You are browsing {window.location.hostname} without an account. Sign in
          with an admin Google account to open it.
        </p>
        <button
          type="button"
          onClick={go}
          disabled={busy}
          className="mt-5 w-full rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign in with Google'}
        </button>
        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
      </div>
    </div>
  )
}

function BootScreen({ message, error }: { message: string; error?: boolean }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-950 text-sm text-white/80">
      <div
        className={
          'rounded-md px-4 py-2 ring-1 ' +
          (error ? 'bg-rose-900/60 ring-rose-500/40 text-rose-100' : 'bg-slate-900/70 ring-white/10')
        }
      >
        {message}
      </div>
    </div>
  )
}
